import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import TabbedImageUploadModal from '@/components/ui/upload/TabbedImageUploadModal';
import { useAuth } from '@/hooks/auth';
import { useProfilePhoto } from '@/hooks/profile/use-profile-photo';
import { toast } from '@/hooks/errors/use-toast';

const Welcome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { uploadProfilePhoto } = useProfilePhoto();
  const [isModalOpen, setIsModalOpen] = useState(true);

  useEffect(() => {
    // If user is not authenticated, redirect to signin
    if (!user) {
      navigate('/signin');
      return;
    }
  }, [user, navigate]);

  const handlePhotoUpload = async (file: File): Promise<string> => {
    try {
      const uploadedUrl = await uploadProfilePhoto(file);
      
      toast({
        title: "Welcome to FinalPiece.ai!",
        description: "Your profile photo has been set. Let's get started!",
        variant: "success",
      });
      
      return uploadedUrl;
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      throw error;
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    // Navigate to home after modal closes
    setTimeout(() => {
      navigate('/home');
    }, 300);
  };

  const handleSkip = () => {
    toast({
      title: "Welcome to FinalPiece.ai!",
      description: "You can always add a profile photo later from your account settings.",
      variant: "success",
    });
    navigate('/home');
  };

  if (!user) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 flex items-center justify-center relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        {/* Primary floating orb - top left */}
        <motion.div
          className="absolute top-20 left-20 w-96 h-96 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.4, 0.6, 0.4],
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Secondary floating orb - bottom right */}
        <motion.div
          className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-l from-purple-600/15 to-blue-600/15 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.3, 0.5, 0.3],
            x: [0, -40, 0],
            y: [0, 40, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Accent orb - center right */}
        <motion.div
          className="absolute top-1/2 right-32 w-48 h-48 bg-gradient-to-bl from-indigo-500/25 to-blue-500/25 rounded-full blur-2xl"
          animate={{
            scale: [0.8, 1.1, 0.8],
            opacity: [0.2, 0.4, 0.2],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Small accent orb - top center */}
        <motion.div
          className="absolute top-32 left-1/2 w-32 h-32 bg-gradient-to-tr from-purple-400/30 to-pink-400/20 rounded-full blur-xl"
          animate={{
            scale: [1, 0.7, 1],
            opacity: [0.5, 0.8, 0.5],
            x: [-20, 20, -20],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      {/* Profile Photo Upload Modal */}
      <TabbedImageUploadModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onUpload={handlePhotoUpload}
        title="Create Your Avatar! 🎨"
        description="Let's make something fun and unique for your profile"
        maxSizeMB={10} // 10MB - WebP conversion handles optimization
        allowedTypes={['image/jpeg', 'image/png', 'image/webp']}
        aspectRatio={1}
        previewSize="md"
        accentColor="purple"
        autoUpload={true}
        showProgress={true}
        mode="avatar"
        initialDescription="professional headshot, business attire, clean background"
        isOnboarding={true}
      />
    </div>
  );
};

export default Welcome; 